import camelCase from 'camelcase';
import { ColorId } from "../../../types/src/lib/common";
import { Complaint, SalesDocument } from '@icc/b2b/shared';

export const MATH_ENUM_FUNCTIONS = {
    '*': (x, y) => Number(x) * Number(y),
    '+': (x, y) => Number(x) + Number(y),
    '-': (x, y) => Number(x) - Number(y),
    '/': (x, y) => Number(x) / Number(y),
}

export function getColorUid(color: ColorId) {
    return color.id + '+' + Number(color.RAL);
}

export function isUndefined(value: any): value is undefined {
    return typeof value === 'undefined';
}

export function isDefined(value: any) {
    return typeof value !== 'undefined';
}

export function isBoolean(value: any): value is boolean {
    return typeof value === 'boolean';
}

export function isString(value: any): value is string {
    return typeof value === 'string';
}

export function isNumber(n){
    return !isNaN(parseFloat(n)) && isFinite(n) && !Array.isArray(n);
}

export function isObject<T>(value: null | undefined | string | number | symbol | boolean | any[] | T): value is T {
    return value !== null && typeof value === 'object';
}

export function isNotNullOrUndefined<T>(value: null | undefined | T): value is T {
    return value != null;
}

export function isEmptyObject(value: any): value is {} {
    return isObject(value) && Object.keys(value).length === 0;
}

export function castToNullable(value: any) {
    return value === 'null' ? null : value
}

export function castToNullableNumber(value: any) {
    return (value === null) ? null : Number(value);
}

export function isValidDate(date: any): date is Date {
    return date && Object.prototype.toString.call(date) === "[object Date]" && !isNaN(date);
}


export const isArray = Array.isArray;

export const hasOwnProperty = Object.prototype.hasOwnProperty;

export function isBlankObject(value) {
    return value !== null && typeof value === 'object' && !Object.getPrototypeOf(value);
}

export function isDate(value) {
    return toString.call(value) === '[object Date]';
}

export function isFunction(value) {
    return typeof value === 'function';
}

export function isRegExp(value) {
    return toString.call(value) === '[object RegExp]';
}

export function isWindow(obj) {
    return obj && obj.window === obj;
}

export function isElement(node) {
    return !!(
        node
        && (node.nodeName  // We are a direct element.
            || (node.prop && node.attr && node.find))
    ); // We have an on and find method part of jQuery API.
}
export function isArrayLike(obj) {
    // `null`, `undefined` and `window` are not array-like
    if (obj == null || isWindow(obj)) {
        return false;
    }

    // arrays, strings and jQuery/jqLite objects are array like
    // * jqLite is either the jQuery or jqLite constructor function
    // * we have to check the existence of jqLite first as this method is called
    //   via the forEach method when constructing the jqLite object in the first place
    if (isArray(obj) || isString(obj)) {
        return true;
    }

    // Support: iOS 8.2 (not reproducible in simulator)
    // "length" in obj used to prevent JIT error (gh-11508)
    const length = 'length' in Object(obj) && obj.length;

    // NodeList objects (with `item` method) and
    // other objects with suitable length characteristics are array-like
    return (
        isNumber(length)
        && ((length >= 0 && (length - 1 in obj || obj instanceof Array))
            || typeof obj.item === 'function')
    );
}

export function isTypedArray(value) {
    const TYPED_ARRAY_REGEXP = /^\[object (?:Uint8|Uint8Clamped|Uint16|Uint32|Int8|Int16|Int32|Float32|Float64)Array]$/;
    return (
        value && isNumber(value.length) && TYPED_ARRAY_REGEXP.test(toString.call(value))
    );
}

export function isArrayBuffer(obj) {
    return toString.call(obj) === '[object ArrayBuffer]';
}

export function isError(what) {
    var toString = Object.prototype.toString.call(what);
    return isObject(what) &&
        toString === '[object Error]' ||
        toString === '[object Exception]' || // Firefox NS_ERROR_FAILURE Exceptions
        what instanceof Error;
}

export function baseExtend(dst, objs, deep) {
    for (let i = 0, ii = objs.length; i < ii; ++i) {
        const obj = objs[i];
        if (!isObject(obj) && !isFunction(obj)) {
            continue;
        }
        const keys = Object.keys(obj);
        for (let j = 0, jj = keys.length; j < jj; j++) {
            const key = keys[j];
            const src = obj[key];

            if (deep && isObject(src)) {
                if (isDate(src)) {
                    dst[key] = new Date(src.valueOf());
                } else if (isRegExp(src)) {
                    dst[key] = new RegExp(src);
                } else if (src.nodeName) {
                    dst[key] = src.cloneNode(true);
                } else if (isElement(src)) {
                    dst[key] = src.clone();
                } else {
                    if (!isObject(dst[key])) {
                        dst[key] = isArray(src) ? [] : {};
                    }
                    baseExtend(dst[key], [src], true);
                }
            } else {
                dst[key] = src;
            }
        }
    }

    return dst;
}

export function extend(dst, ...src) {
    return baseExtend(dst, src, false);
}

export function forEach<T, U extends ArrayLike<T> = T[]>(
    obj: U,
    iterator: (value: U[number], key: number | '$$hashKey', obj: U) => void,
    context?: any
): U;
export function forEach<T>(
    obj: { [index: string]: T },
    iterator: (value: T, key: string, obj: { [index: string]: T }) => void,
    context?: any
): { [index: string]: T };
export function forEach(
    obj: any,
    iterator: (value: any, key: any, obj: any) => void,
    context?: any
): any {
    let key, length;
    if (obj) {
        if (isFunction(obj)) {
            for (key in obj) {
                // Need to check if hasOwnProperty exists,
                // as on IE8 the result of querySelectorAll is an object without a hasOwnProperty function
                if (
                    key !== 'prototype'
                    && key !== 'length'
                    && key !== 'name'
                    && (!obj.hasOwnProperty || obj.hasOwnProperty(key))
                ) {
                    iterator.call(context, obj[key], key, obj);
                }
            }
        } else if (isArray(obj) || isArrayLike(obj)) {
            const isPrimitive = typeof obj !== 'object';
            for (key = 0, length = obj.length; key < length; key++) {
                if (isPrimitive || key in obj) {
                    iterator.call(context, obj[key], key, obj);
                }
            }
        } else if (obj.forEach && obj.forEach !== forEach) {
            obj.forEach(iterator, context, obj);
        } else if (isBlankObject(obj)) {
            // createMap() fast path --- Safe to avoid hasOwnProperty check because prototype chain is empty
            for (key in obj) {
                iterator.call(context, obj[key], key, obj);
            }
        } else if (typeof obj.hasOwnProperty === 'function') {
            // Slow path for objects inheriting Object.prototype, hasOwnProperty check needed
            for (key in obj) {
                if (obj.hasOwnProperty(key)) {
                    iterator.call(context, obj[key], key, obj);
                }
            }
        } else {
            // Slow path for objects which do not have a method `hasOwnProperty`
            for (key in obj) {
                if (hasOwnProperty.call(obj, key)) {
                    iterator.call(context, obj[key], key, obj);
                }
            }
        }
    }
    return obj;
}

export function equals(o1, o2) {
    if (o1 === o2) {
        return true;
    }
    if (o1 === null || o2 === null) {
        return false;
    }
    // eslint-disable-next-line no-self-compare
    if (o1 !== o1 && o2 !== o2) {
        return true;
    } // NaN === NaN
    let t1 = typeof o1,
        t2 = typeof o2,
        length,
        key,
        keySet;
    if (t1 === t2 && t1 === 'object') {
        if (isArray(o1)) {
            if (!isArray(o2)) {
                return false;
            }
            if ((length = o1.length) === o2.length) {
                for (key = 0; key < length; key++) {
                    if (!equals(o1[key], o2[key])) {
                        return false;
                    }
                }
                return true;
            }
        } else if (isDate(o1)) {
            if (!isDate(o2)) {
                return false;
            }
            return equals(o1.getTime(), o2.getTime());
        } else if (isRegExp(o1)) {
            if (!isRegExp(o2)) {
                return false;
            }
            return o1.toString() === o2.toString();
        } else {
            if (
                isWindow(o1)
                || isWindow(o2)
                || isArray(o2)
                || isDate(o2)
                || isRegExp(o2)
            ) {
                return false;
            }
            keySet = Object.create(null);
            for (key in o1) {
                if (key.charAt(0) === '$' || isFunction(o1[key])) {
                    continue;
                }
                if (!equals(o1[key], o2[key])) {
                    return false;
                }
                keySet[key] = true;
            }
            for (key in o2) {
                if (
                    !(key in keySet)
                    && key.charAt(0) !== '$'
                    && isDefined(o2[key])
                    && !isFunction(o2[key])
                ) {
                    return false;
                }
            }
            return true;
        }
    }
    return false;
}

export function deepCopy<T>(source: T, destination?, maxDepth?): T {
    const stackSource = [];
    const stackDest = [];
    maxDepth = isValidObjectMaxDepth(maxDepth) ? maxDepth : NaN;

    if (destination) {
        if (isTypedArray(destination) || isArrayBuffer(destination)) {
            throw Error("Can't copy! TypedArray destination cannot be mutated.");
        }
        if (source === destination) {
            throw Error("Can't copy! Source and destination are identical.");
        }

        // Empty the destination object
        if (isArray(destination)) {
            destination.length = 0;
        } else {
            forEach(destination, (value, key) => {
                if (key !== '$$hashKey') {
                    delete destination[key];
                }
            });
        }

        stackSource.push(source);
        stackDest.push(destination);
        return copyRecurse(source, destination, maxDepth);
    }

    return copyElement(source, maxDepth);
}

export function humanFileSize(size) {
    const i = size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
    return (size / Math.pow(1024, i)).toFixed(2) + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
}

export function availableStorage() {
    const anyNav = navigator as any;
    if ('storage' in anyNav && 'estimate' in anyNav.storage) {
        return anyNav.storage.estimate();
    }
    return Promise.resolve(null);
}

export function merge(...dst) {
    return baseExtend(dst, [].slice.call(arguments, 1), true);
}
export const stackSource = [];
export const stackDest = [];

export function isValidObjectMaxDepth(maxDepth) {
    return isNumber(maxDepth) && maxDepth > 0;
}

export function copyElement(source, maxDepth?) {
    // Simple values
    if (!isObject(source)) {
        return source;
    }

    // Already copied values
    const index = stackSource.indexOf(source);
    if (index !== -1) {
        return stackDest[index];
    }

    if (isWindow(source)) {
        throw Error("Can't copy! Making copies of Window or Scope instances is not supported.");
    }

    let needsRecurse = false;
    let destination = copyType(source);

    if (destination === undefined) {
        destination = isArray(source) ? [] : Object.create(Object.getPrototypeOf(source));
        needsRecurse = true;
    }

    stackSource.push(source);
    stackDest.push(destination);

    return needsRecurse ? copyRecurse(source, destination, maxDepth) : destination;
}

export function copyType(source) {
    switch (toString.call(source)) {
        case '[object Int8Array]':
        case '[object Int16Array]':
        case '[object Int32Array]':
        case '[object Float32Array]':
        case '[object Float64Array]':
        case '[object Uint8Array]':
        case '[object Uint8ClampedArray]':
        case '[object Uint16Array]':
        case '[object Uint32Array]':
            return new source.constructor(
                copyElement(source.buffer),
                source.byteOffset,
                source.length
            );

        case '[object ArrayBuffer]':
            // Support: IE10
            if (!source.slice) {
                // If we're in this case we know the environment supports ArrayBuffer
                /* eslint-disable no-undef */
                const copied = new ArrayBuffer(source.byteLength);
                new Uint8Array(copied).set(new Uint8Array(source));
                /* eslint-enable */
                return copied;
            }
            return source.slice(0);

        case '[object Boolean]':
        case '[object Number]':
        case '[object String]':
        case '[object Date]':
            return new source.constructor(source.valueOf());

        case '[object RegExp]':
            const re = new RegExp(source.source, source.toString().match(/[^/]*$/)[0]);
            re.lastIndex = source.lastIndex;
            return re;

        case '[object Blob]':
            return new source.constructor([source], { type: source.type });
    }

    if (isFunction(source.cloneNode)) {
        return source.cloneNode(true);
    }
}

export function copyRecurse(source, destination, maxDepth) {
    maxDepth--;
    if (maxDepth < 0) {
        return '...';
    }
    let key;
    if (isArray(source)) {
        for (let i = 0, ii = source.length; i < ii; i++) {
            destination.push(copyElement(source[i], maxDepth));
        }
    } else if (isBlankObject(source)) {
        // createMap() fast path --- Safe to avoid hasOwnProperty check because prototype chain is empty
        for (key in source) {
            destination[key] = copyElement(source[key], maxDepth);
        }
    } else if (source && typeof source.hasOwnProperty === 'function') {
        // Slow path, which must rely on hasOwnProperty
        for (key in source) {
            if (source.hasOwnProperty(key)) {
                destination[key] = copyElement(source[key], maxDepth);
            }
        }
    } else {
        // Slowest path --- hasOwnProperty can't be called as a method
        for (key in source) {
            if (hasOwnProperty.call(source, key)) {
                destination[key] = copyElement(source[key], maxDepth);
            }
        }
    }
    return destination;
}

type Required<T> =
  T extends object
    ? { [P in keyof T]-?: NonNullable<T[P]>; }
    : T;


export interface Point {
    x: number;
    y: number;
    cr?: number;
}

export function _(str: string) {
    return str;
}

export function cameCaseKeys(data) {
    const result: any = Object.create(data);
    Object.keys(data).forEach((key) => {
        result[camelCase(key)] = data[key];
    });
    return result;
}

export function isSalesDocument(value: any): value is SalesDocument {
    return !value.startsWith('c-');
}

export function isComplaintDocument(value: any): value is Complaint {
    return value.startsWith('c-');
}